/**
 * Session Management Service
 * Comprehensive session management with token refresh, persistence, and security
 */

import axios from 'axios';
import { updateTokens, logout } from '../features/auth/authSlice';
import API_CONFIG from '../config/apiConfig';

class SessionManager {
    constructor(store = null) {
        this.store = store;
        this.isRefreshing = false;
        this.refreshSubscribers = [];
        this.refreshPromise = null;
        this.sessionTimeout = null;
        this.heartbeatInterval = null;
        this.maxRetries = 3;
        this.retryCount = 0;

        // Session configuration
        this.config = {
            refreshThreshold: 5 * 60 * 1000, // 5 minutes before expiry
            sessionTimeout: 24 * 60 * 60 * 1000, // 24 hours of inactivity (much more lenient)
            heartbeatInterval: 10 * 60 * 1000, // 10 minutes heartbeat (less frequent)
            maxRetries: 3,
            retryDelay: 1000, // 1 second
        };

        if (this.store) {
            this.initialize();
        }
    }

    /**
     * Set the Redux store
     */
    setStore(store) {
        this.store = store;
        if (this.store) {
            this.initialize();
        }
    }

    /**
     * Initialize session management
     */
    initialize() {
        if (!this.store) {
            console.warn('SessionManager: No store available, cannot initialize');
            return;
        }

        this.setupEventListeners();
        this.restoreSession();
        
        // Only start session management if we have valid tokens
        const state = this.store.getState();
        const { accessToken, refreshToken } = state.auth;
        
        if (accessToken && refreshToken) {
            this.startSessionTimeout();
            this.startHeartbeat();
        }
    }

    /**
     * Setup event listeners for session management
     */
    setupEventListeners() {
        // Listen for visibility changes
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                this.handleVisibilityChange();
            }
        });

        // Listen for focus events
        window.addEventListener('focus', () => {
            this.handleWindowFocus();
        });

        // Listen for beforeunload events
        window.addEventListener('beforeunload', () => {
            this.handleBeforeUnload();
        });

        // Listen for storage events (multi-tab sync)
        window.addEventListener('storage', (e) => {
            this.handleStorageChange(e);
        });

        // Listen for online/offline events
        window.addEventListener('online', () => {
            this.handleOnlineStatus();
        });

        window.addEventListener('offline', () => {
            this.handleOfflineStatus();
        });
    }

    /**
     * Restore session from localStorage
     * Only restores if tokens exist - doesn't force logout for public users
     */
    restoreSession() {
        if (!this.store) {
            console.warn('SessionManager: No store available, cannot restore session');
            return;
        }

        try {
            const accessToken = localStorage.getItem('accessToken');
            const refreshToken = localStorage.getItem('refreshToken');
            const userStr = localStorage.getItem('user');
            const user = userStr ? JSON.parse(userStr) : null;

            if (accessToken && refreshToken && user) {
                // Validate token expiry
                if (this.isTokenExpired(accessToken)) {
                    if (this.isTokenExpired(refreshToken)) {
                        console.warn('Both access and refresh tokens are expired - clearing session');
                        this.clearSession();
                        return;
                    }
                    // Refresh the access token
                    this.refreshAccessToken().catch(error => {
                        console.warn('Failed to refresh access token during session restoration:', error);
                        // Only clear session if refresh token is actually expired
                        const isTokenExpired = error.response?.status === 401 || 
                                             error.response?.data?.code === 'token_not_valid';
                        if (isTokenExpired) {
                            this.clearSession();
                        } else {
                            // Keep session even if refresh fails (might be network issue)
                            // User can retry later
                            console.warn('Keeping session despite refresh failure - might be temporary');
                        }
                    });
                } else {
                    // Update Redux state only if tokens are valid
                    this.store.dispatch(updateTokens({
                        user,
                        accessToken,
                        refreshToken
                    }));
                }
            }
            // If no tokens exist, just don't restore - don't clear anything
            // This allows public users to use the app without being forced to login
        } catch (error) {
            console.error('Session restoration failed:', error);
            // Only clear session if there was an actual error parsing stored data
            // Don't force clear for users without tokens (public users)
            if (localStorage.getItem('accessToken') || localStorage.getItem('refreshToken')) {
                this.clearSession();
            }
        }
    }

    /**
     * Check if token is expired
     * Add buffer to prevent false positives
     */
    isTokenExpired(token) {
        if (!token) return true;

        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            const currentTime = Date.now() / 1000;
            // Add 60 second buffer to prevent false expiration
            const buffer = 60;
            return payload.exp < (currentTime - buffer);
        } catch (error) {
            // If we can't parse, don't assume expired - might be invalid format
            console.warn('Token parsing error (not treating as expired):', error);
            return false;
        }
    }

    /**
     * Get token expiry time
     */
    getTokenExpiry(token) {
        if (!token) return null;

        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            return new Date(payload.exp * 1000);
        } catch (error) {
            return null;
        }
    }

    /**
     * Check if token needs refresh
     */
    needsRefresh(token) {
        if (!token) return true;

        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            const currentTime = Date.now() / 1000;
            const timeUntilExpiry = payload.exp - currentTime;

            // Refresh if token expires within the threshold
            return timeUntilExpiry < (this.config.refreshThreshold / 1000);
        } catch (error) {
            return true;
        }
    }

    /**
     * Refresh access token
     */
    async refreshAccessToken() {
        if (this.isRefreshing) {
            return this.refreshPromise;
        }

        this.isRefreshing = true;
        this.refreshPromise = this.performTokenRefresh();

        try {
            const result = await this.refreshPromise;
            return result;
        } catch (error) {
            console.error('Token refresh failed:', error);
            // DON'T clear session on refresh failure - might be temporary network issue
            // Only clear if refresh token is actually expired
            const isTokenExpired = error.response?.status === 401 || 
                                 error.response?.data?.code === 'token_not_valid';
            if (isTokenExpired) {
                this.clearSession();
            }
            throw error;
        } finally {
            this.isRefreshing = false;
            this.refreshPromise = null;
        }
    }

    /**
     * Perform the actual token refresh
     */
    async performTokenRefresh() {
        if (!this.store) {
            console.warn('SessionManager: No store available, cannot refresh token');
            return null;
        }

        const state = this.store.getState();
        const { refreshToken } = state.auth;

        if (!refreshToken) {
            console.warn('No refresh token available - clearing session');
            this.clearSession();
            return null;
        }

        try {
            const response = await axios.post(
                `${API_CONFIG.baseURL}/auth/token/refresh/`, { refresh: refreshToken }
            );

            const { access } = response.data;

            // Update tokens in Redux and localStorage
            this.store.dispatch(updateTokens({
                user: state.auth.user,
                accessToken: access,
                refreshToken: refreshToken
            }));

            // Notify all waiting requests
            this.notifyRefreshSubscribers(access);

            // Reset retry count on successful refresh
            this.retryCount = 0;

            return access;
        } catch (error) {
            console.error('Token refresh failed:', error);

            // Check if refresh token is actually expired
            const isTokenExpired = error.response?.status === 401 || 
                                 error.response?.data?.code === 'token_not_valid';

            // Handle refresh failure
            if (this.retryCount < this.maxRetries && !isTokenExpired) {
                // Retry if not token expired (might be network issue)
                this.retryCount++;
                await this.delay(this.config.retryDelay * this.retryCount);
                return this.performTokenRefresh();
            } else {
                // Only clear session if token is actually expired
                if (isTokenExpired) {
                    this.clearSession();
                }
                throw error;
            }
        }
    }

    /**
     * Notify all waiting requests
     */
    notifyRefreshSubscribers(token) {
        this.refreshSubscribers.forEach(callback => callback(token));
        this.refreshSubscribers = [];
    }

    /**
     * Subscribe to token refresh
     */
    subscribeToRefresh(callback) {
        this.refreshSubscribers.push(callback);
    }

    /**
     * Handle API request with automatic token refresh
     */
    async handleApiRequest(requestFn) {
        if (!this.store) {
            throw new Error('SessionManager: No store available');
        }

        const state = this.store.getState();
        const { accessToken } = state.auth;

        if (!accessToken) {
            throw new Error('No access token available');
        }

        // Check if token needs refresh
        if (this.needsRefresh(accessToken)) {
            if (this.isRefreshing) {
                // Wait for ongoing refresh
                return new Promise((resolve) => {
                    this.subscribeToRefresh((newToken) => {
                        resolve(requestFn(newToken));
                    });
                });
            } else {
                // Start refresh
                const newToken = await this.refreshAccessToken();
                return requestFn(newToken);
            }
        }

        return requestFn(accessToken);
    }

    /**
     * Start session timeout
     */
    startSessionTimeout() {
        this.clearSessionTimeout();

        this.sessionTimeout = setTimeout(() => {
            this.handleSessionTimeout();
        }, this.config.sessionTimeout);
    }

    /**
     * Clear session timeout
     */
    clearSessionTimeout() {
        if (this.sessionTimeout) {
            clearTimeout(this.sessionTimeout);
            this.sessionTimeout = null;
        }
    }

    /**
     * Handle session timeout
     */
    handleSessionTimeout() {
        console.log('Session timeout - checking if user is still active');
        // Only timeout if refresh token is also expired
        if (!this.store) {
            this.clearSession();
            return;
        }

        const state = this.store.getState();
        const { refreshToken } = state.auth;

        // Only clear session if refresh token is expired
        // If refresh token is still valid, just refresh access token
        if (refreshToken && this.isTokenExpired(refreshToken)) {
            console.log('Refresh token expired - logging out user');
            this.clearSession();
        } else if (refreshToken) {
            // Refresh token is still valid - refresh access token and reset timeout
            console.log('Refresh token still valid - refreshing access token');
            this.refreshAccessToken().catch(error => {
                console.warn('Failed to refresh after timeout:', error);
            });
            this.startSessionTimeout();
        } else {
            this.clearSession();
        }
    }

    /**
     * Start heartbeat
     */
    startHeartbeat() {
        this.clearHeartbeat();

        this.heartbeatInterval = setInterval(() => {
            this.performHeartbeat();
        }, this.config.heartbeatInterval);
    }

    /**
     * Clear heartbeat
     */
    clearHeartbeat() {
        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
            this.heartbeatInterval = null;
        }
    }

    /**
     * Perform heartbeat check
     */
    async performHeartbeat() {
        if (!this.store) {
            return;
        }

        const state = this.store.getState();
        const { accessToken, refreshToken } = state.auth;

        if (!accessToken || !refreshToken) {
            // No tokens available - don't clear session, might just be loading
            // Only clear if we're sure user was logged out
            console.warn('No tokens in heartbeat - skipping');
            return;
        }

        try {
            // Check if token needs refresh
            if (this.needsRefresh(accessToken)) {
                await this.refreshAccessToken();
            }
            // Reset session timeout on successful heartbeat
            this.startSessionTimeout();
        } catch (error) {
            console.error('Heartbeat failed:', error);
            // DON'T clear session on heartbeat failure - might be temporary network issue
            // Just log and continue - user is still authenticated
        }
    }

    /**
     * Handle visibility change
     */
    handleVisibilityChange() {
        if (document.visibilityState === 'visible') {
            this.startSessionTimeout();
            this.performHeartbeat();
        }
    }

    /**
     * Handle window focus
     */
    handleWindowFocus() {
        this.startSessionTimeout();
        this.performHeartbeat();
    }

    /**
     * Handle before unload
     */
    handleBeforeUnload() {
        // Save any pending data
        this.saveSessionState();
    }

    /**
     * Handle storage change (multi-tab sync)
     */
    handleStorageChange(event) {
        if (event.key === 'accessToken' || event.key === 'refreshToken' || event.key === 'user') {
            // Sync session across tabs
            this.restoreSession();
        }
    }

    /**
     * Handle online status
     */
    handleOnlineStatus() {
        console.log('Back online - performing heartbeat');
        this.performHeartbeat();
    }

    /**
     * Handle offline status
     */
    handleOfflineStatus() {
        console.log('Gone offline - pausing session management');
        this.clearHeartbeat();
    }

    /**
     * Save session state
     */
    saveSessionState() {
        if (!this.store) {
            return;
        }

        const state = this.store.getState();
        const { accessToken, refreshToken, user } = state.auth;

        if (accessToken && refreshToken && user) {
            localStorage.setItem('accessToken', accessToken);
            localStorage.setItem('refreshToken', refreshToken);
            localStorage.setItem('user', JSON.stringify(user));
        }
    }

    /**
     * Clear session
     */
    clearSession() {
        // Clear Redux state if store is available
        if (this.store) {
            this.store.dispatch(logout());
        }

        // Clear localStorage
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');

        // Clear timers
        this.clearSessionTimeout();
        this.clearHeartbeat();

        // Reset state
        this.isRefreshing = false;
        this.refreshSubscribers = [];
        this.refreshPromise = null;
        this.retryCount = 0;
    }

    /**
     * Get session info
     */
    getSessionInfo() {
        if (!this.store) {
            return {
                isAuthenticated: false,
                accessToken: null,
                refreshToken: null,
                user: null,
                accessTokenExpiry: null,
                refreshTokenExpiry: null,
                needsRefresh: true,
                isRefreshing: false
            };
        }

        const state = this.store.getState();
        const { accessToken, refreshToken, user } = state.auth;

        return {
            isAuthenticated: !!(accessToken && refreshToken && user),
            accessToken,
            refreshToken,
            user,
            accessTokenExpiry: this.getTokenExpiry(accessToken),
            refreshTokenExpiry: this.getTokenExpiry(refreshToken),
            needsRefresh: this.needsRefresh(accessToken),
            isRefreshing: this.isRefreshing
        };
    }

    /**
     * Update session configuration
     */
    updateConfig(newConfig) {
        this.config = {...this.config, ...newConfig };

        // Restart timers with new configuration
        this.clearSessionTimeout();
        this.clearHeartbeat();
        this.startSessionTimeout();
        this.startHeartbeat();
    }

    /**
     * Start session management (called when tokens are updated)
     */
    startSessionManagement() {
        if (!this.store) {
            console.warn('SessionManager: No store available, cannot start session management');
            return;
        }

        const state = this.store.getState();
        const { accessToken, refreshToken } = state.auth;
        
        if (accessToken && refreshToken) {
            this.startSessionTimeout();
            this.startHeartbeat();
        }
    }

    /**
     * Delay utility
     */
    delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    /**
     * Destroy session manager
     */
    destroy() {
        this.clearSessionTimeout();
        this.clearHeartbeat();
        this.clearSession();
    }
}

// Create singleton instance (store will be set later)
const sessionManager = new SessionManager();

export default sessionManager;